import { Box } from '@chakra-ui/layout';
import { useLayoutEffect, useRef } from 'react';

import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { ReportWrapper } from '../ContentGrid/ReportWrapper';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import { scrollPositionStore } from '../scroll-position-store';
import { DASHBOARD_ACTION_BAR_HEIGHT } from './constants';
import { DashboardEditableGrid } from './DashboardEditableGrid';
import { GridProvider } from './GridProvider';
import { ReportEditableGrid } from './ReportEditableGrid';

interface EditableGridProps {
  onBuilderOpenChange?: (isOpen: boolean) => void;
  onAIDrawerOpenChange?: (isOpen: boolean) => void;
}

export const EditableGrid = ({
  onBuilderOpenChange,
  onAIDrawerOpenChange,
}: EditableGridProps) => {
  const { config: pageConfig } = usePageConfig();
  const { config } = useTabContentConfig();
  const { items } = config;
  const { pdfExportRef } = usePageExport();
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);

  const isReport = pageConfig.type === PageType.REPORT;
  const gridRows = config.grid?.rows ?? DEFAULT_GRID_ROWS;
  const gridColumns = config.grid?.columns ?? DEFAULT_GRID_COLUMNS;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    scrollPositionStore.restore(scrollContainerRef.current);
  }, []);

  return (
    <Box
      ref={scrollContainerRef}
      width="100%"
      {...(isNewDashboardLayout
        ? { flex: '1' }
        : { height: `calc(100% - ${DASHBOARD_ACTION_BAR_HEIGHT})` })}
      minWidth="0"
      minHeight="0"
      overflow="auto"
      onScroll={(e) => scrollPositionStore.save(e.currentTarget)}
      {...(isReport && { borderRadius: 'lg' })}
      sx={{
        overflowAnchor: 'none',
        scrollbarGutter: 'stable',
        overscrollBehavior: 'contain',
        scrollbarWidth: 'thin',
        scrollbarColor: '#E3E3E3 transparent',
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          borderRadius: '10px',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '&:hover::-webkit-scrollbar-thumb': {
          backgroundColor: '#E3E3E3',
        },
      }}
    >
      <ReportWrapper isReport={isReport}>
        <Box ref={pdfExportRef} width="100%" height="100%">
          <GridProvider
            items={items}
            gridRows={gridRows}
            gridColumns={gridColumns}
            isReport={isReport}
            gridContainerRef={gridContainerRef}
          >
            {isReport ? (
              <ReportEditableGrid
                onBuilderOpenChange={onBuilderOpenChange}
                onAIDrawerOpenChange={onAIDrawerOpenChange}
                gridContainerRef={gridContainerRef}
              />
            ) : (
              <DashboardEditableGrid
                onBuilderOpenChange={onBuilderOpenChange}
                onAIDrawerOpenChange={onAIDrawerOpenChange}
                gridContainerRef={gridContainerRef}
              />
            )}
          </GridProvider>
        </Box>
      </ReportWrapper>
    </Box>
  );
};
